"use client";

import { useState, useEffect, useMemo } from "react";
import { observer } from "mobx-react-lite";
import { useAppContext } from "../../../../shared/functions/Context";
import { dateFormat_YY_MM_DY_timeStamp } from "../../../shared/utils/utils";
import type { ILeadershipRating } from "../../../../shared/models/three-sixty-feedback-models/LeadershipRating";
import {
  FLAG_APPROVAL_EMAIL,
  FLAG_REJECT_EMAIL,
  MAIL_EMAIL,
  RESUBMISSION_EMAIL,
} from "../../../../shared/functions/mailMessages";
import {
  Flag,
  Download,
  Printer,
  Search,
  Calendar,
  ChevronLeft,
  ChevronRight,
  FileText,
  Crown,
  Heart,
  Briefcase,
  CheckCircle,
  Loader2,
  MessageSquare,
  XCircle,
} from "lucide-react";

interface ILeadershipRatings {
  overallRating: number;
  id: string;
  rateeId: string;
  dimension: string;
  isflagged: boolean;
  department: string;
  description: string;
  userComment?: string;
  adminComment?: string;
  adminApproval?: string;
  criteria: {
    [raterId: string]: {
      additionalComment?: string;
      stopDoing: string;
      startDoing: string;
      continueDoing: string;
      ratings: {
        [criteriaName: string]: {
          [statement: string]: number;
        };
      };
      leadershipScore?: number;
    };
  };
  date: number;
  raterId?: string;
  templateId?: string;
  heading?: string;
  reasonForRequest?: string;
  dueDate?: number;
  values?: {};
}

interface IProp {
  data: (ILeadershipRating | ILeadershipRatings)[];
  description: string;
}

const AdminFlagRatingsTable = observer(({ data, description }: IProp) => {
  const { api, store, ui } = useAppContext();
  const me = store.auth.meJson?.uid;
  const user = store.user.selected;
  const templates = store.templates.all.map((template) => template.asJson);

  const [selectedRow, setSelectedRow] = useState<ILeadershipRatings | null>(
    null
  );
  const [showAdminActionDialog, setShowAdminActionDialog] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [isProcessing, setIsProcessing] = useState(false);
  const [adminComment, setAdminComment] = useState("");
  const [adminAction, setAdminAction] = useState<
    "approve-rerating" | "reject" | null
  >(null);

  const fetchData = async () => {
    await api.user.getAll();
    await api.templates.getAll();
    await api.ratingAssignments.getAll();
  };

  useEffect(() => {
    fetchData();
  }, [api.user, api.templates]);

  const getTemplateTitle = (templateId: string): string => {
    const foundTemplate = templates.find(
      (template) => template.id === templateId
    );
    return foundTemplate ? foundTemplate.title : templateId;
  };

  const getDimensionIcon = (dimension: string) => {
    if (dimension === "Values")
      return <Heart className="h-4 w-4 text-blue-600" />;
    if (dimension === "Leadership")
      return <Crown className="h-4 w-4 text-purple-600" />;
    return <Briefcase className="h-4 w-4 text-green-600" />;
  };

  // Filter and search data
  const filteredData = useMemo(() => {
    return data.filter((item) => {
      const matchesSearch =
        searchTerm === "" ||
        item.dimension.toLowerCase().includes(searchTerm.toLowerCase()) ||
        getTemplateTitle(item.dimension)
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        item.userComment?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.adminComment?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        Object.values(item.criteria).some(
          (criteria) =>
            criteria.startDoing
              ?.toLowerCase()
              .includes(searchTerm.toLowerCase()) ||
            criteria.stopDoing
              ?.toLowerCase()
              .includes(searchTerm.toLowerCase()) ||
            criteria.continueDoing
              ?.toLowerCase()
              .includes(searchTerm.toLowerCase()) ||
            criteria.additionalComment
              ?.toLowerCase()
              .includes(searchTerm.toLowerCase())
        );
      return matchesSearch;
    });
  }, [data, searchTerm]);

  // Pagination
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedData = filteredData.slice(
    startIndex,
    startIndex + itemsPerPage
  );

  // Handle admin actions
  const handleAdminAction = (
    row: ILeadershipRatings,
    action: "approve-rerating" | "reject"
  ) => {
    setSelectedRow(row);
    setAdminAction(action);
    setAdminComment("");
    setShowAdminActionDialog(true);
  };

  const confirmAdminAction = async () => {
    if (!selectedRow || !adminComment.trim() || !adminAction) {
      ui.snackbar.load({
        id: Date.now(),
        type: "warning",
        message: "Comment is required for admin actions.",
        timeoutInMs: 5000,
      });
      return;
    }

    setIsProcessing(true);
    try {
      let updatedRating: any;

      if (adminAction === "approve-rerating") {
        // Approve and trigger re-rating process
        if (selectedRow.dimension === "Values") {
          const checkInstore = store.valueRating.getItemById(
            selectedRow.id
          )?.asJson;
          if (checkInstore) {
            updatedRating = {
              ...checkInstore,
              adminApproval: "Approved for Re-rating",
              adminComment: adminComment.trim(),
              resubmission: "To be rated",
              department: selectedRow.department,
              values: selectedRow.criteria,
            };
            await api.valueRating.update(
              updatedRating,
              updatedRating.rateeId,
              description
            );

            // Send re-rating email
            const { MY_SUBJECT, MY_BODY } = FLAG_APPROVAL_EMAIL();

            try {
              await api.mail.sendMail(
                ["systems@lotsinsights.com"],
                MAIL_EMAIL,
                [],
                MY_SUBJECT,
                MY_BODY
              );
              ui.snackbar.load({
                id: Date.now(),
                type: "success",
                message:
                  "Rating approved for re-rating and email sent successfully.",
                timeoutInMs: 10000,
              });
            } catch (error) {
              console.log("Error sending email:", error);
              ui.snackbar.load({
                id: Date.now(),
                type: "warning",
                message: "Rating approved for re-rating but email not sent.",
                timeoutInMs: 10000,
              });
            }
          }
        } else if (selectedRow.dimension === "Leadership") {
          const checkInstore = store.leadershipRating.getItemById(
            selectedRow.id
          )?.asJson;
          if (checkInstore) {
            updatedRating = {
              ...checkInstore,
              adminApproval: "Approved for Re-rating",
              adminComment: adminComment.trim(),
              resubmission: "To be rated",
              department: selectedRow.department,
              criteria: selectedRow.criteria,
            };
            await api.leadershipRating.update(
              updatedRating,
              updatedRating.rateeId,
              description
            );

            // Send re-rating email
            // Send re-rating email
            const { MY_SUBJECT, MY_BODY } = FLAG_APPROVAL_EMAIL();

            try {
              await api.mail.sendMail(
                ["systems@lotsinsights.com"],
                MAIL_EMAIL,
                [],
                MY_SUBJECT,
                MY_BODY
              );
              ui.snackbar.load({
                id: Date.now(),
                type: "success",
                message:
                  "Rating approved for re-rating and email sent successfully.",
                timeoutInMs: 10000,
              });
            } catch (error) {
              console.log("Error sending email:", error);
              ui.snackbar.load({
                id: Date.now(),
                type: "warning",
                message: "Rating approved for re-rating but email not sent.",
                timeoutInMs: 10000,
              });
            }
          }
        } else {
          updatedRating = {
            ...selectedRow,
            adminApproval: "Approved for Re-rating",
            adminComment: adminComment.trim(),
            resubmission: "To be rated",
            department: selectedRow.department,
          };
          await api.templateRating.update(updatedRating);

          // Send re-rating email
          // Send re-rating email
          const { MY_SUBJECT, MY_BODY } = FLAG_APPROVAL_EMAIL();

          try {
            await api.mail.sendMail(
              ["systems@lotsinsights.com"],
              MAIL_EMAIL,
              [],
              MY_SUBJECT,
              MY_BODY
            );
            ui.snackbar.load({
              id: Date.now(),
              type: "success",
              message:
                "Template rating approved for re-rating and email sent successfully.",
              timeoutInMs: 10000,
            });
          } catch (error) {
            console.log("Error sending email:", error);
            ui.snackbar.load({
              id: Date.now(),
              type: "warning",
              message:
                "Template rating approved for re-rating but email not sent.",
              timeoutInMs: 10000,
            });
          }
        }
      } else if (adminAction === "reject") {
        // Just reject with comment
        if (selectedRow.dimension === "Values") {
          const checkInstore = store.valueRating.getItemById(
            selectedRow.id
          )?.asJson;
          if (checkInstore) {
            updatedRating = {
              ...checkInstore,
              adminApproval: "Rejected",
              adminComment: adminComment.trim(),
              department: selectedRow.department,
              values: selectedRow.criteria,
              isflagged: false,
            };
            await api.valueRating.update(
              updatedRating,
              updatedRating.rateeId,
              description
            );
            // Send re-rating email
            const { MY_SUBJECT, MY_BODY } = FLAG_REJECT_EMAIL();
            try {
              await api.mail.sendMail(
                ["systems@lotsinsights.com"],
                MAIL_EMAIL,
                [],
                MY_SUBJECT,
                MY_BODY
              );
              ui.snackbar.load({
                id: Date.now(),
                type: "success",
                message:
                  "Rating rejected for re-rating and email sent successfully.",
                timeoutInMs: 10000,
              });
            } catch (error) {
              console.log("Error sending email:", error);
              ui.snackbar.load({
                id: Date.now(),
                type: "warning",
                message: "Rating rejected for re-rating but email not sent.",
                timeoutInMs: 10000,
              });
            }
          }
        } else if (selectedRow.dimension === "Leadership") {
          const checkInstore = store.leadershipRating.getItemById(
            selectedRow.id
          )?.asJson;
          if (checkInstore) {
            updatedRating = {
              ...checkInstore,
              adminApproval: "Rejected",
              adminComment: adminComment.trim(),
              department: selectedRow.department,
              criteria: selectedRow.criteria,
              isflagged: false,
            };
            await api.leadershipRating.update(
              updatedRating,
              updatedRating.rateeId,
              description
            );
            // Send re-rating email
            const { MY_SUBJECT, MY_BODY } = FLAG_REJECT_EMAIL();
            try {
              await api.mail.sendMail(
                ["systems@lotsinsights.com",],
                MAIL_EMAIL,
                [],
                MY_SUBJECT,
                MY_BODY
              );
              ui.snackbar.load({
                id: Date.now(),
                type: "success",
                message:
                  "Rating rejected for re-rating and email sent successfully.",
                timeoutInMs: 10000,
              });
            } catch (error) {
              console.log("Error sending email:", error);
              ui.snackbar.load({
                id: Date.now(),
                type: "warning",
                message: "Rating rejected for re-rating but email not sent.",
                timeoutInMs: 10000,
              });
            }
          }
        } else {
          updatedRating = {
            ...selectedRow,
            adminApproval: "Rejected",
            adminComment: adminComment.trim(),
            department: selectedRow.department,
            isflagged: false,
          };
          await api.templateRating.update(updatedRating);
          // Send re-rating email
          const { MY_SUBJECT, MY_BODY } = FLAG_REJECT_EMAIL();
          try {
            await api.mail.sendMail(
              ["systems@lotsinsights.com"],
              MAIL_EMAIL,
              [],
              MY_SUBJECT,
              MY_BODY
            );
            ui.snackbar.load({
              id: Date.now(),
              type: "success",
              message:
                "Rating rejected for re-rating and email sent successfully.",
              timeoutInMs: 10000,
            });
          } catch (error) {
            console.log("Error sending email:", error);
            ui.snackbar.load({
              id: Date.now(),
              type: "warning",
              message: "Rating rejected for re-rating but email not sent.",
              timeoutInMs: 10000,
            });
          }
        }

        ui.snackbar.load({
          id: Date.now(),
          type: "success",
          message: "Rating rejected successfully.",
          timeoutInMs: 5000,
        });
      }

      setShowAdminActionDialog(false);
      setSelectedRow(null);
      setAdminComment("");
      setAdminAction(null);
    } catch (error) {
      console.error("Error updating rating:", error);
      ui.snackbar.load({
        id: Date.now(),
        type: "warning",
        message: "Failed to update rating. Please try again.",
        timeoutInMs: 5000,
      });
    } finally {
      setIsProcessing(false);
    }
  };
  const handleExportPDF = () => {
    console.log("Export PDF functionality");
  };

  const handlePrint = () => {
    console.log("Print functionality");
  };

  return (
    <div className="space-y-8">
      {/* Header and Controls */}
      <div className="bg-white rounded-2xl shadow-lg border border-slate-200/50 p-6">
        <div className="flex flex-col lg:flex-row gap-4 justify-between items-start lg:items-center border-b border-slate-200 pb-6 mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 rounded-lg">
              <Flag className="h-5 w-5 text-red-600" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-slate-800">
                Admin Flagged Ratings
              </h3>
              <p className="text-slate-600">
                Review and manage flagged ratings with admin actions
              </p>
            </div>
          </div>
          <div className="flex gap-3">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search ratings..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              />
            </div>
            {/* Export buttons */}
            <button
              onClick={handlePrint}
              className="p-2 text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors"
              title="Print"
            >
              <Printer className="h-5 w-5" />
            </button>
            <button
              onClick={handleExportPDF}
              className="p-2 text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors"
              title="Export PDF"
            >
              <Download className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Results summary */}
        <div className="flex items-center justify-between text-sm text-slate-600 mb-4">
          <span>
            Showing {startIndex + 1}-
            {Math.min(startIndex + itemsPerPage, filteredData.length)} of{" "}
            {filteredData.length} ratings
          </span>
          <select
            value={itemsPerPage}
            onChange={(e) => {
              setItemsPerPage(Number(e.target.value));
              setCurrentPage(1);
            }}
            className="px-2 py-1 border border-slate-300 rounded text-sm"
          >
            <option value={5}>5 per page</option>
            <option value={10}>10 per page</option>
            <option value={25}>25 per page</option>
            <option value={50}>50 per page</option>
          </select>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                  Dimension
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                  Start Doing
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                  Stop Doing
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                  Continue Doing
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                  User Comment
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                  Admin Comment
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {paginatedData.map((row, index) => {
                const criteria =
                  row.criteria && Object.keys(row.criteria).length > 0
                    ? row.criteria[Object.keys(row.criteria)[0]]
                    : null;
                return (
                  <tr
                    key={row.id}
                    className={`hover:bg-slate-50 transition-colors ${
                      index % 2 === 0 ? "bg-white" : "bg-slate-25"
                    }`}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-slate-400" />
                        <span className="text-sm text-slate-900">
                          {dateFormat_YY_MM_DY_timeStamp(row.date)}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        {getDimensionIcon(row.dimension)}
                        <span className="text-sm font-medium text-slate-900">
                          {row.dimension === "Values" ||
                          row.dimension === "Leadership"
                            ? row.dimension
                            : getTemplateTitle(row.dimension)}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div
                        className="text-sm text-slate-900 max-w-xs truncate"
                        title={criteria?.startDoing || ""}
                      >
                        {criteria?.startDoing || "-"}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div
                        className="text-sm text-slate-900 max-w-xs truncate"
                        title={criteria?.stopDoing || ""}
                      >
                        {criteria?.stopDoing || "-"}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div
                        className="text-sm text-slate-900 max-w-xs truncate"
                        title={criteria?.continueDoing || ""}
                      >
                        {criteria?.continueDoing || "-"}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div
                        className="text-sm text-slate-900 max-w-xs truncate"
                        title={row.adminComment || ""}
                      >
                        {row.userComment ? (
                          <div className="flex items-center gap-2">
                            <MessageSquare className="h-3 w-3 text-blue-500" />
                            <span>{row.userComment}</span>
                          </div>
                        ) : (
                          "-"
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div
                        className="text-sm text-slate-900 max-w-xs truncate"
                        title={row.adminComment || ""}
                      >
                        {row.adminComment ? (
                          <div className="flex items-center gap-2">
                            <MessageSquare className="h-3 w-3 text-purple-500" />
                            <span>{row.adminComment}</span>
                          </div>
                        ) : (
                          "-"
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <div
                          className={`flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold ${
                            row.isflagged
                              ? "bg-red-100 text-red-800"
                              : "bg-slate-100 text-slate-800"
                          }`}
                        >
                          <Flag className="h-3 w-3" />
                          {row.isflagged ? "Flagged" : "Normal"}
                        </div>
                        {row.adminApproval && (
                          <div
                            className={`px-2 py-1 rounded-full text-xs font-medium ${
                              row.adminApproval === "Approved for Re-rating"
                                ? "bg-green-100 text-green-800"
                                : row.adminApproval === "Rejected"
                                ? "bg-red-100 text-red-800"
                                : "bg-blue-100 text-blue-800"
                            }`}
                          >
                            {row.adminApproval}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        {/* Admin Actions for flagged items */}
                        {row.isflagged && !row.adminApproval && (
                          <>
                            <button
                              onClick={() =>
                                handleAdminAction(row, "approve-rerating")
                              }
                              disabled={isProcessing}
                              className="px-3 py-1 bg-green-100 text-green-700 hover:bg-green-200 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 disabled:opacity-50"
                              title="Approve for Re-rating"
                            >
                              <CheckCircle className="h-3 w-3" />
                              Approve Re-rating
                            </button>
                            <button
                              onClick={() => handleAdminAction(row, "reject")}
                              disabled={isProcessing}
                              className="px-3 py-1 bg-red-100 text-red-700 hover:bg-red-200 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 disabled:opacity-50"
                              title="Reject Rating"
                            >
                              <XCircle className="h-3 w-3" />
                              Reject
                            </button>
                          </>
                        )}
                        {row.adminApproval && (
                          <div className="text-sm text-slate-500 italic">
                            {row.adminApproval === "Approved for Re-rating"
                              ? "Approved for re-rating"
                              : "Rejected"}
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* Empty state */}
          {paginatedData.length === 0 && (
            <div className="text-center py-12">
              <FileText className="h-12 w-12 text-slate-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-900 mb-2">
                No ratings found
              </h3>
              <p className="text-slate-600">
                Try adjusting your search criteria.
              </p>
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-slate-200 mt-6">
            <div className="text-sm text-slate-600">
              Page {currentPage} of {totalPages}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="p-2 text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              {/* Page numbers */}
              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const pageNum =
                    Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i;
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`px-3 py-1 rounded-lg text-sm transition-colors ${
                        currentPage === pageNum
                          ? "bg-red-600 text-white"
                          : "text-slate-600 hover:text-slate-800 hover:bg-slate-100"
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>
              <button
                onClick={() =>
                  setCurrentPage(Math.min(totalPages, currentPage + 1))
                }
                disabled={currentPage === totalPages}
                className="p-2 text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Admin Action Dialog */}
      {showAdminActionDialog && selectedRow && adminAction && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl">
            <div className="flex items-center gap-3 mb-4">
              <div
                className={`p-3 rounded-full ${
                  adminAction === "approve-rerating"
                    ? "bg-green-100"
                    : "bg-red-100"
                }`}
              >
                {adminAction === "approve-rerating" ? (
                  <CheckCircle className="h-6 w-6 text-green-600" />
                ) : (
                  <XCircle className="h-6 w-6 text-red-600" />
                )}
              </div>
              <h3 className="text-xl font-semibold text-slate-900">
                {adminAction === "approve-rerating"
                  ? "Approve for Re-rating"
                  : "Reject Rating"}
              </h3>
            </div>

            <p className="text-slate-600 mb-4">
              {adminAction === "approve-rerating"
                ? "This will approve the rating for re-rating and send an email notification to the ratee."
                : "This will reject the flagged rating."}
              <br />
              Please provide a comment for this action:
            </p>

            <div className="mb-6">
              <label
                htmlFor="adminComment"
                className="block text-sm font-medium text-slate-700 mb-2"
              >
                Admin Comment <span className="text-red-500">*</span>
              </label>
              <textarea
                id="adminComment"
                value={adminComment}
                onChange={(e) => setAdminComment(e.target.value)}
                placeholder={`Please explain why you are ${
                  adminAction === "approve-rerating"
                    ? "approving this for re-rating"
                    : "rejecting this rating"
                }...`}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                rows={3}
                required
              />
            </div>

            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  setShowAdminActionDialog(false);
                  setAdminComment("");
                  setAdminAction(null);
                  setSelectedRow(null);
                }}
                disabled={isProcessing}
                className="px-6 py-3 text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors font-medium disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmAdminAction}
                disabled={isProcessing || !adminComment.trim()}
                className={`px-6 py-3 text-white rounded-lg transition-colors font-medium disabled:opacity-50 flex items-center gap-2 ${
                  adminAction === "approve-rerating"
                    ? "bg-green-600 hover:bg-green-700"
                    : "bg-red-600 hover:bg-red-700"
                }`}
              >
                {isProcessing ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    {adminAction === "approve-rerating" ? (
                      <CheckCircle className="h-4 w-4" />
                    ) : (
                      <XCircle className="h-4 w-4" />
                    )}
                    {adminAction === "approve-rerating"
                      ? "Approve Re-rating"
                      : "Reject"}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

export default AdminFlagRatingsTable;
